/**
 * 병의원·약국 공공 데이터 수집 배치 수동 실행 버튼 + 확인 모달.
 * 병의원 관리·문전약국 관리 두 목록 화면 헤더에서 같이 쓴다.
 *
 * - 서버가 중복 실행을 막지 않으므로 확인 모달을 거치고, 요청 중에는 실행 버튼을 잠근다
 * - 202 는 "접수"일 뿐이라 완료·실패는 Slack 배치 알림 채널로 확인하도록 안내한다
 */
import { isInvalidBaseYmFormatError } from '@/entities/facility-collection/api/facility-collection.api';
import { useRunFacilityCollectionJob } from '@/entities/facility-collection/hooks/useFacilityCollection';
import { MonthPicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { isAxiosError } from 'axios';
import { AlertCircle, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';

export const FacilityCollectionRunButton: React.FC = () => {
  const toast = useToast();
  const runJob = useRunFacilityCollectionJob();
  const [open, setOpen] = useState(false);
  // 비우면 서버가 현재 연월로 실행한다
  const [baseYm, setBaseYm] = useState('');

  const handleOpen = () => {
    setBaseYm('');
    setOpen(true);
  };

  const handleRun = async () => {
    if (runJob.isPending) return;
    try {
      await runJob.mutateAsync(baseYm || undefined);
      toast.success(
        '배치 실행을 요청했습니다. 완료까지 시간이 걸릴 수 있습니다.',
      );
      setOpen(false);
    } catch (error) {
      // 401 은 apiClient 인터셉터가 공통 처리한다
      if (isInvalidBaseYmFormatError(error)) {
        toast.error('기준 연월 형식이 올바르지 않습니다. (예: 202610)');
      } else if (isAxiosError(error) && error.response?.status === 403) {
        toast.error('실행 권한이 없습니다.');
      } else {
        handleApiError(error, toast);
      }
    }
  };

  return (
    <MenuWriteGate>
      <Button variant="outline" onClick={handleOpen}>
        <RefreshCw className="mr-2 h-4 w-4" />
        공공 데이터 수집
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => !v && !runJob.isPending && setOpen(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>병의원·약국 공공 데이터 수집</DialogTitle>
            <DialogDescription>
              심평원 병의원·약국 기본목록과 공공 인허가 정보를 다시 수집해
              병의원·약국 정보, 영업 상태, 진료과목에 반영합니다.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1">
            <Label htmlFor="facility-collection-ym">수집 기준 연월</Label>
            <MonthPicker
              id="facility-collection-ym"
              value={baseYm}
              onChange={setBaseYm}
              placeholder="현재 연월"
              disabled={runJob.isPending}
              className="h-10 w-[160px]"
            />
            <p className="text-xs text-muted-foreground">
              비워 두면 현재 연월로 실행합니다.
            </p>
          </div>

          <ul className="space-y-1 text-xs text-ruda-warning">
            <li className="flex items-start gap-1.5">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
              정기 수집(월·수·금 새벽)이 실패했을 때만 실행해 주세요. 이미 실행
              중이어도 서버가 막지 않아 중복 실행됩니다.
            </li>
            <li className="flex items-start gap-1.5">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
              진행 상태는 화면에서 확인할 수 없습니다. 완료·실패 결과는 Slack
              배치 알림 채널로 확인하세요.
            </li>
          </ul>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={runJob.isPending}
            >
              취소
            </Button>
            <Button
              onClick={() => void handleRun()}
              disabled={runJob.isPending}
            >
              {runJob.isPending ? '요청 중...' : '실행'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MenuWriteGate>
  );
};
